/**
 * Servicio API para import/export de calendarios iCal
 * @module services/icalService
 */

import { api } from './api';
import type {
  ICalExportParams,
  ICalImportParams,
  ICalImportResult,
} from '@/types/calendar.types';

class ICalService {
  private readonly basePath = '/crm/appointments/ical';

  /**
   * Exportar citas a formato iCal
   */
  async export(params?: ICalExportParams): Promise<Blob> {
    const response = await api.get<Blob>(`${this.basePath}/export`, {
      params,
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * Descargar archivo iCal
   */
  async downloadICalFile(params?: ICalExportParams, filename?: string): Promise<void> {
    const blob = await this.export(params);
    
    // Crear URL temporal
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Nombre del archivo
    const defaultFilename = `calendario-migro-${new Date().toISOString().split('T')[0]}.ics`;
    link.download = filename || defaultFilename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Cleanup
    window.URL.revokeObjectURL(url);
  }

  /**
   * Importar citas desde archivo iCal
   */
  async import(file: File, params?: ICalImportParams): Promise<ICalImportResult> {
    const formData = new FormData();
    formData.append('ical_file', file);
    
    // Agregar parámetros opcionales
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          formData.append(key, String(value));
        }
      });
    }

    const response = await api.post<ICalImportResult>(
      `${this.basePath}/import`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    
    return response.data;
  }
}

export const icalService = new ICalService();
export default icalService;
